import { supabase } from './supabase';
import {
  Team,
  Workspace,
  TeamMember,
  SharedSummary,
  WorkspaceComment,
  UserTeam,
  WorkspaceSummary,
  CreateTeamRequest,
  InviteTeamMemberRequest,
  CreateWorkspaceRequest,
  ShareSummaryRequest
} from '../types';

/** A pending team invitation for an email that may not have an account yet. */
export interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invited_at: string;
  expires_at: string;
}

export class TeamWorkspaceService {
  private static instance: TeamWorkspaceService;

  static getInstance(): TeamWorkspaceService {
    if (!TeamWorkspaceService.instance) {
      TeamWorkspaceService.instance = new TeamWorkspaceService();
    }
    return TeamWorkspaceService.instance;
  }

  /**
   * Create a new team
   */
  async createTeam(request: CreateTeamRequest): Promise<Team> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a team');
    }

    const { data, error } = await supabase.rpc('create_team', {
      team_name: request.name,
      team_description: request.description || null
    });

    if (error) {
      throw new Error(`Failed to create team: ${error.message}`);
    }

    // Return a basic team object since we know the creation succeeded
    // The team will be fetched properly when the page reloads
    return {
      id: data,
      name: request.name,
      description: request.description || undefined,
      owner_id: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as Team;
  }

  /**
   * Get all teams for the current user
   */
  async getUserTeams(): Promise<UserTeam[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('get_user_teams');

    if (error) {
      throw new Error(`Failed to fetch user teams: ${error.message}`);
    }

    return data as UserTeam[];
  }

  /**
   * Get team details by ID
   */
  async getTeam(teamId: string): Promise<Team> {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', teamId)
      .single();

    if (error || !data) {
      throw new Error('Team not found or access denied');
    }

    return data as Team;
  }

  /**
   * Get team members
   */
  async getTeamMembers(teamId: string): Promise<TeamMember[]> {
    // Use RPC function to avoid RLS recursion issues
    const { data, error } = await supabase.rpc('get_team_members', {
      team_uuid: teamId
    });

    if (error) {
      throw new Error(`Failed to fetch team members: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Get user emails in batch
    const userIds = [...new Set(data.map((m: any) => m.user_id))];
    const { data: userEmails, error: emailError } = await supabase.rpc('get_user_emails_batch', {
      user_uuids: userIds
    });

    if (emailError) {
      throw new Error(`Failed to fetch user emails: ${emailError.message}`);
    }

    // Create a map for quick lookup
    const userMap = new Map((userEmails || []).map((ue: { user_id: string; email: string }) => [ue.user_id, ue.email]));

    // Add email to each member
    return data.map((member: any) => ({
      ...member,
      users: { email: userMap.get(member.user_id) || 'Unknown' }
    })) as TeamMember[];
  }

  /**
   * Pending (not-yet-accepted) invitations for a team the caller owns.
   * Backed by the owner-scoped get_team_invitations RPC (the table has RLS
   * enabled with no direct-select policy).
   */
  async getTeamInvitations(teamId: string): Promise<TeamInvitation[]> {
    const { data, error } = await supabase.rpc('get_team_invitations', { team_uuid: teamId });
    if (error) throw new Error(`Failed to load pending invitations: ${error.message}`);
    return (data || []) as TeamInvitation[];
  }

  /** Cancel a pending team invitation (owner only). */
  async cancelTeamInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase.rpc('cancel_team_invitation', { invitation_id: invitationId });
    if (error) throw new Error(`Failed to cancel invitation: ${error.message}`);
  }

  /**
   * Invite a user to a team
   */
  async inviteTeamMember(request: InviteTeamMemberRequest): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('invite_team_member', {
      team_uuid: request.teamId,
      user_email: request.userEmail,
      member_role: request.role || 'member'
    });

    if (error) {
      throw new Error(`Failed to invite team member: ${error.message}`);
    }

    // Send email notification (non-blocking - don't fail invitation if email fails)
    try {
      // Get team details
      const team = await this.getTeam(request.teamId);
      
      // Get inviter's email
      const { data: { user: inviterUser } } = await supabase.auth.getUser();
      const inviterEmail = inviterUser?.email || '';
      
      // Call email notification function
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email-notification`;
      await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: request.userEmail,
          emailType: 'team_invitation',
          teamName: team.name,
          teamDescription: team.description || '',
          teamId: request.teamId,
          inviterEmail: inviterEmail,
          inviterName: inviterEmail.split('@')[0], // Use email prefix as name
          role: request.role || 'member'
        }),
      });
    } catch (emailError) {
      // Log error but don't fail the invitation
      console.error('Failed to send invitation email:', emailError);
      // Invitation was successful, so we don't throw
    }
  }

  /**
   * Accept a team invitation
   */
  async acceptTeamInvitation(teamId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('accept_team_invitation', {
      team_uuid: teamId
    });

    if (error) {
      throw new Error(`Failed to accept team invitation: ${error.message}`);
    }
  }

  /**
   * Decline a team invitation
   */
  async declineTeamInvitation(teamId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Remove the team member record for declined invitation
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', user.id)
      .eq('status', 'invited');

    if (error) {
      throw new Error(`Failed to decline team invitation: ${error.message}`);
    }
  }

  /**
   * Create a workspace (team-based or standalone)
   */
  async createWorkspace(request: CreateWorkspaceRequest): Promise<Workspace> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a workspace');
    }

    const { data, error } = await supabase.rpc('create_workspace', {
      workspace_name: request.name,
      docket_num: request.docketNumber,
      team_uuid: request.teamId || null,
      workspace_description: request.description || null
    });

    if (error) {
      throw new Error(`Failed to create workspace: ${error.message}`);
    }

    // Fetch the created workspace using RPC function to bypass RLS issues
    const { data: workspaceData, error: fetchError } = await supabase.rpc('get_workspace', {
      workspace_uuid: data
    });

    if (fetchError || !workspaceData || workspaceData.length === 0) {
      throw new Error(`Failed to fetch created workspace: ${fetchError?.message || 'Workspace not found'}`);
    }

    return workspaceData[0] as Workspace;
  }

  /**
   * Create a standalone workspace (no team required)
   */
  async createStandaloneWorkspace(name: string, docketNumber: string, description?: string): Promise<Workspace> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a workspace');
    }

    const { data, error } = await supabase.rpc('create_workspace_standalone', {
      workspace_name: name,
      docket_num: docketNumber,
      workspace_description: description || null
    });

    if (error) {
      throw new Error(`Failed to create workspace: ${error.message}`);
    }

    // Fetch the created workspace using RPC function to bypass RLS issues
    const { data: workspaceData, error: fetchError } = await supabase.rpc('get_workspace', {
      workspace_uuid: data
    });

    if (fetchError || !workspaceData || workspaceData.length === 0) {
      throw new Error(`Failed to fetch created workspace: ${fetchError?.message || 'Workspace not found'}`);
    }

    return workspaceData[0] as Workspace;
  }

  /**
   * Get all workspaces user has access to (team or direct)
   */
  async getUserWorkspaces(): Promise<Array<Workspace & { user_role: string; access_type: string }>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('get_user_workspaces');

    if (error) {
      throw new Error(`Failed to fetch user workspaces: ${error.message}`);
    }

    return (data || []) as Array<Workspace & { user_role: string; access_type: string }>;
  }

  /**
   * Get workspaces for a team
   */
  async getTeamWorkspaces(teamId: string): Promise<Workspace[]> {
    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch team workspaces: ${error.message}`);
    }

    return data as Workspace[];
  }

  /**
   * Get workspace details
   */
  async getWorkspace(workspaceId: string): Promise<Workspace> {
    // Use RPC function to bypass RLS issues
    const { data, error } = await supabase.rpc('get_workspace', {
      workspace_uuid: workspaceId
    });

    if (error || !data || data.length === 0) {
      throw new Error(`Workspace not found or access denied: ${error?.message || 'Not found'}`);
    }

    return data[0] as Workspace;
  }

  /**
   * Update a workspace
   */
  async updateWorkspace(workspaceId: string, updates: { name?: string; description?: string }): Promise<Workspace> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Use RPC function if it exists, otherwise direct update
    const { data, error } = await supabase.rpc('update_workspace', {
      workspace_uuid: workspaceId,
      workspace_name: updates.name || null,
      workspace_description: updates.description || null
    }).catch(async () => {
      // Fallback to direct update if RPC doesn't exist
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      updateData.updated_at = new Date().toISOString();

      return await supabase
        .from('workspaces')
        .update(updateData)
        .eq('id', workspaceId)
        .select()
        .single();
    });

    if (error) {
      throw new Error(`Failed to update workspace: ${error.message}`);
    }

    // If RPC returns UUID, fetch the workspace
    if (typeof data === 'string') {
      const { data: workspace, error: fetchError } = await supabase
        .from('workspaces')
        .select('*')
        .eq('id', data)
        .single();

      if (fetchError || !workspace) {
        throw new Error('Failed to fetch updated workspace');
      }

      return workspace as Workspace;
    }

    return data as Workspace;
  }

  /**
   * Delete a workspace (only for team owners or workspace owners)
   */
  async deleteWorkspace(workspaceId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Try direct deletion with RLS
    const { error: deleteError } = await supabase
      .from('workspaces')
      .delete()
      .eq('id', workspaceId);

    if (deleteError) {
      throw new Error(`Failed to delete workspace: ${deleteError.message}`);
    }
  }

  /**
   * Share a summary in a workspace
   */
  async shareSummary(request: ShareSummaryRequest): Promise<SharedSummary> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('share_summary_in_workspace', {
      workspace_uuid: request.workspaceId,
      summary_uuid: request.summaryId,
      access_level: request.accessLevel || 'read',
      summary_notes: request.notes || null
    });

    if (error) {
      throw new Error(`Failed to share summary: ${error.message}`);
    }

    // Log activity
    await supabase.rpc('log_workspace_activity', {
      workspace_uuid: request.workspaceId,
      activity_type_param: 'summary_shared',
      activity_data_param: {
        shared_summary_id: data,
        summary_id: request.summaryId,
        access_level: request.accessLevel || 'read'
      }
    });

    // Fetch the created shared summary
    const { data: sharedSummary, error: fetchError } = await supabase
      .from('shared_summaries')
      .select('*')
      .eq('id', data)
      .single();

    if (fetchError || !sharedSummary) {
      throw new Error('Failed to fetch shared summary');
    }

    return sharedSummary as SharedSummary;
  }

  /**
   * Get summaries shared in a workspace
   */
  async getWorkspaceSummaries(workspaceId: string): Promise<WorkspaceSummary[]> {
    // Use RPC function to bypass RLS recursion issues
    const { data: sharedSummaries, error: sharedError } = await supabase.rpc('get_workspace_summaries', {
      workspace_uuid: workspaceId
    });

    if (sharedError) {
      throw new Error(`Failed to fetch workspace summaries: ${sharedError.message}`);
    }

    if (!sharedSummaries || sharedSummaries.length === 0) {
      return [];
    }

    // Get the personalized summaries for these shared summaries
    const summaryIds = sharedSummaries.map(ss => ss.original_summary_id);
    const { data: personalizedSummaries, error: personalError } = await supabase
      .from('personalized_summaries')
      .select('*')
      .in('id', summaryIds);

    if (personalError) {
      throw new Error(`Failed to fetch personalized summaries: ${personalError.message}`);
    }

    // Create a map for quick lookup
    const summaryMap = new Map(personalizedSummaries?.map(ps => [ps.id, ps]) || []);
    
    // Get user emails in batch
    const userIds = [...new Set(sharedSummaries.map(ss => ss.shared_by))];
    const { data: userEmails, error: emailError } = await supabase.rpc('get_user_emails_batch', {
      user_uuids: userIds
    });

    if (emailError) {
      throw new Error(`Failed to fetch user emails: ${emailError.message}`);
    }

    // Create a map for quick lookup
    const userMap = new Map((userEmails || []).map((ue: { user_id: string; email: string }) => [ue.user_id, ue.email]));

    // Combine the data
    return sharedSummaries.map(ss => {
      const summary = summaryMap.get(ss.original_summary_id);
      const user = userMap.get(ss.shared_by);
      
      return {
        shared_summary_id: ss.id,
        original_summary_id: ss.original_summary_id,
        summary_topics: summary?.topics || [],
        summary_type: summary?.summary_type || '',
        filing_type: summary?.filing_type || '',
        document_count: summary?.document_count || 0,
        shared_by: ss.shared_by,
        shared_by_email: userMap.get(ss.shared_by) || 'Unknown',
        shared_at: ss.shared_at,
        access_level: ss.access_level,
        notes: ss.notes
      };
    }) as WorkspaceSummary[];
  }

  /**
   * Get shared summary content
   */
  async getSharedSummaryContent(sharedSummaryId: string): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get the shared summary to find the original summary
    const { data: sharedSummary, error: sharedError } = await supabase
      .from('shared_summaries')
      .select('original_summary_id')
      .eq('id', sharedSummaryId)
      .single();

    if (sharedError || !sharedSummary) {
      throw new Error('Shared summary not found or access denied');
    }

    // Get the original summary to find the storage path
    const { data: originalSummary, error: originalError } = await supabase
      .from('personalized_summaries')
      .select('storage_path')
      .eq('id', sharedSummary.original_summary_id)
      .single();

    if (originalError || !originalSummary) {
      throw new Error('Original summary not found');
    }

    // Download the summary content from storage
    const { data: content, error: downloadError } = await supabase.storage
      .from('documents')
      .download(`${originalSummary.storage_path}/summary.docx`);

    if (downloadError || !content) {
      throw new Error('Failed to download summary content');
    }

    return await content.text();
  }

  /**
   * Add a comment to a workspace
   */
  async addWorkspaceComment(workspaceId: string, content: string, sharedSummaryId?: string): Promise<WorkspaceComment> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('workspace_comments')
      .insert({
        workspace_id: workspaceId,
        shared_summary_id: sharedSummaryId,
        user_id: user.id,
        content
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to add comment: ${error.message}`);
    }

    // Log activity
    await supabase.rpc('log_workspace_activity', {
      workspace_uuid: workspaceId,
      activity_type_param: 'comment_added',
      activity_data_param: {
        comment_id: data.id,
        shared_summary_id: sharedSummaryId || null
      }
    });

    return data as WorkspaceComment;
  }

  /**
   * Get comments for a workspace
   */
  async getWorkspaceComments(workspaceId: string, sharedSummaryId?: string): Promise<WorkspaceComment[]> {
    // Use RPC function to bypass RLS recursion issues
    const { data: comments, error } = await supabase.rpc('get_workspace_comments', {
      workspace_uuid: workspaceId
    });

    if (error) {
      throw new Error(`Failed to fetch workspace comments: ${error.message}`);
    }

    if (!comments || comments.length === 0) {
      return [];
    }

    // Filter by shared_summary_id if provided
    let filteredComments = comments;
    if (sharedSummaryId) {
      filteredComments = comments.filter((c: any) => c.shared_summary_id === sharedSummaryId);
    } else {
      filteredComments = comments.filter((c: any) => !c.shared_summary_id);
    }

    // Get user emails in batch
    const userIds = [...new Set(filteredComments.map((c: any) => c.user_id))];
    const { data: userEmails, error: emailError } = await supabase.rpc('get_user_emails_batch', {
      user_uuids: userIds
    });

    if (emailError) {
      throw new Error(`Failed to fetch user emails: ${emailError.message}`);
    }

    // Create a map for quick lookup
    const userMap = new Map((userEmails || []).map((ue: { user_id: string; email: string }) => [ue.user_id, ue.email]));

    // Add email to each comment
    return filteredComments.map((comment: any) => ({
      ...comment,
      user_email: userMap.get(comment.user_id) || 'Unknown'
    })) as WorkspaceComment[];
  }

  /**
   * Update a comment
   */
  async updateWorkspaceComment(commentId: string, content: string): Promise<WorkspaceComment> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('workspace_comments')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', commentId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update comment: ${error.message}`);
    }

    return data as WorkspaceComment;
  }

  /**
   * Delete a comment
   */
  async deleteWorkspaceComment(commentId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { error } = await supabase
      .from('workspace_comments')
      .delete()
      .eq('id', commentId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to delete comment: ${error.message}`);
    }
  }

  /**
   * Remove a shared summary from workspace
   */
  async removeSharedSummary({ sharedSummaryId, workspaceId }: { sharedSummaryId: string; workspaceId: string }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { error } = await supabase.rpc('remove_shared_summary', {
      shared_summary_uuid: sharedSummaryId,
      workspace_uuid: workspaceId,
    });

    if (error) {
      throw new Error(`Failed to remove shared summary: ${error.message}`);
    }

    // Log activity
    await supabase.rpc('log_workspace_activity', {
      workspace_uuid: workspaceId,
      activity_type_param: 'summary_removed',
      activity_data_param: {
        shared_summary_id: sharedSummaryId
      }
    });
  }

  /**
   * Leave a team
   */
  async leaveTeam(teamId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Check if user is the team owner
    const team = await this.getTeam(teamId);
    if (team.owner_id === user.id) {
      throw new Error('Team owners cannot leave their team. Please transfer ownership or delete the team.');
    }

    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to leave team: ${error.message}`);
    }
  }

  /**
   * Delete a team (only for team owners)
   */
  async deleteTeam(teamId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Use RPC function for team deletion
    const { data, error } = await supabase.rpc('delete_team', {
      team_uuid: teamId
    });

    if (error) {
      throw new Error(`Failed to delete team: ${error.message}`);
    }
  }

  /**
   * Get user's access level in a workspace
   */
  async getUserWorkspaceAccess(workspaceId: string): Promise<{ team_role: string; can_share: boolean; can_comment: boolean } | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('get_workspace_user_access', {
      workspace_uuid: workspaceId,
      user_uuid: user.id
    });

    if (error) {
      throw new Error(`Failed to get workspace access: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return null;
    }

    return data[0] as { team_role: string; can_share: boolean; can_comment: boolean };
  }

  /**
   * Update a team member's role
   */
  async updateMemberRole(teamId: string, memberUserId: string, newRole: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Verify user is team owner
    const team = await this.getTeam(teamId);
    if (team.owner_id !== user.id) {
      throw new Error('Only team owners can change member roles');
    }

    // Don't allow changing owner role
    if (newRole === 'owner') {
      throw new Error('Cannot change role to owner');
    }

    const { error } = await supabase
      .from('team_members')
      .update({ role: newRole })
      .eq('team_id', teamId)
      .eq('user_id', memberUserId);

    if (error) {
      throw new Error(`Failed to update member role: ${error.message}`);
    }
  }

  /**
   * Get workspace collaborators
   */
  async getWorkspaceCollaborators(workspaceId: string): Promise<Array<{
    id: string;
    workspace_id: string;
    user_id: string;
    role: string;
    invited_by?: string;
    invited_at: string;
    joined_at?: string;
    status: string;
    user_email?: string;
  }>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Use RPC function to avoid RLS recursion
    const { data, error } = await supabase.rpc('get_workspace_collaborators', {
      workspace_uuid: workspaceId
    });

    if (error) {
      throw new Error(`Failed to fetch workspace collaborators: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Get user emails in batch
    const userIds = [...new Set(data.map((c: any) => c.user_id))];
    const { data: userEmails, error: emailError } = await supabase.rpc('get_user_emails_batch', {
      user_uuids: userIds
    });

    if (emailError) {
      throw new Error(`Failed to fetch user emails: ${emailError.message}`);
    }

    // Create a map for quick lookup
    const userMap = new Map((userEmails || []).map((ue: { user_id: string; email: string }) => [ue.user_id, ue.email]));

    // Add email to each collaborator
    return data.map((collaborator: any) => ({
      ...collaborator,
      user_email: userMap.get(collaborator.user_id) || 'Unknown'
    }));
  }

  /**
   * Invite a collaborator to a workspace
   */
  async inviteWorkspaceCollaborator(workspaceId: string, userEmail: string, role: 'owner' | 'editor' | 'commenter' | 'viewer' = 'viewer'): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('invite_workspace_collaborator', {
      workspace_uuid: workspaceId,
      user_email: userEmail,
      collaborator_role: role
    });

    if (error) {
      throw new Error(`Failed to invite workspace collaborator: ${error.message}`);
    }

    // Send email notification (non-blocking)
    try {
      const workspace = await this.getWorkspace(workspaceId);
      const { data: { user: inviterUser } } = await supabase.auth.getUser();
      const inviterEmail = inviterUser?.email || '';

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email-notification`;
      await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userEmail,
          emailType: 'workspace_invitation',
          workspaceName: workspace.name,
          workspaceDescription: workspace.description || '',
          workspaceId: workspaceId,
          inviterEmail: inviterEmail,
          inviterName: inviterEmail.split('@')[0],
          role: role
        }),
      });
    } catch (emailError) {
      console.error('Failed to send workspace invitation email:', emailError);
    }
  }

  /**
   * Accept a workspace invitation
   */
  async acceptWorkspaceInvitation(workspaceId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('accept_workspace_invitation', {
      workspace_uuid: workspaceId
    });

    if (error) {
      throw new Error(`Failed to accept workspace invitation: ${error.message}`);
    }
  }

  /**
   * Remove a workspace collaborator
   */
  async removeWorkspaceCollaborator(workspaceId: string, collaboratorId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase.rpc('remove_workspace_collaborator', {
      workspace_uuid: workspaceId,
      collaborator_id: collaboratorId
    });

    if (error) {
      throw new Error(`Failed to remove workspace collaborator: ${error.message}`);
    }
  }

  /**
   * Remove a team member
   */
  async removeTeamMember(teamId: string, memberUserId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Verify user is team owner
    const team = await this.getTeam(teamId);
    if (team.owner_id !== user.id) {
      throw new Error('Only team owners can remove members');
    }

    // Don't allow removing owner
    if (team.owner_id === memberUserId) {
      throw new Error('Cannot remove team owner');
    }

    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', memberUserId);

    if (error) {
      throw new Error(`Failed to remove team member: ${error.message}`);
    }
  }

  /**
   * Export workspace summaries as PDF
   */
  async exportWorkspaceSummaries(workspaceId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get workspace details
    const workspace = await this.getWorkspace(workspaceId);
    
    // Get all summaries
    const summaries = await this.getWorkspaceSummaries(workspaceId);
    
    // Comments feature removed

    // Dynamically import jsPDF
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();

    // Helper function to add text with word wrapping
    const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + (lines.length * (fontSize * 0.4));
    };

    let yPos = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Add title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Workspace Export', margin, yPos);
    yPos += 10;

    // Add workspace info
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    yPos = addWrappedText(`Workspace: ${workspace.name}`, margin, yPos, contentWidth, 12);
    yPos += 2;
    yPos = addWrappedText(`Docket Number: ${workspace.docket_number}`, margin, yPos, contentWidth, 12);
    if (workspace.description) {
      yPos += 2;
      yPos = addWrappedText(`Description: ${workspace.description}`, margin, yPos, contentWidth, 12);
    }
    yPos += 2;
    yPos = addWrappedText(`Export Date: ${new Date().toLocaleString()}`, margin, yPos, contentWidth, 12);
    yPos += 5;

    // Add summaries
    if (summaries.length === 0) {
      yPos = addWrappedText('No summaries shared in this workspace.', margin, yPos, contentWidth);
    } else {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      yPos = addWrappedText('Shared Summaries', margin, yPos, contentWidth, 14);
      yPos += 5;

      for (let index = 0; index < summaries.length; index++) {
        const summary = summaries[index];
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        // Summary header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        yPos = addWrappedText(`Summary ${index + 1}`, margin, yPos, contentWidth, 12);
        yPos += 2;

        // Summary details
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        yPos = addWrappedText(`Topics: ${summary.summary_topics.join(', ')}`, margin, yPos, contentWidth);
        yPos += 2;
        yPos = addWrappedText(`Type: ${summary.summary_type}`, margin, yPos, contentWidth);
        yPos += 2;
        yPos = addWrappedText(`Access Level: ${summary.access_level}`, margin, yPos, contentWidth);
        yPos += 2;
        yPos = addWrappedText(`Shared by: ${summary.shared_by_email}`, margin, yPos, contentWidth);
        yPos += 2;
        yPos = addWrappedText(`Shared at: ${new Date(summary.shared_at).toLocaleString()}`, margin, yPos, contentWidth);
        if (summary.notes) {
          yPos += 2;
          yPos = addWrappedText(`Notes: ${summary.notes}`, margin, yPos, contentWidth);
        }
        yPos += 2;

        // Get summary content
        try {
          const content = await this.getSharedSummaryContent(summary.shared_summary_id);
          if (content) {
            doc.setFont(undefined, 'bold');
            yPos = addWrappedText('Content:', margin, yPos, contentWidth, 10);
            yPos += 2;
            doc.setFont(undefined, 'normal');
            // Clean content for PDF (remove HTML if present, limit length)
            const cleanContent = content.replace(/<[^>]*>/g, '').substring(0, 2000);
            yPos = addWrappedText(cleanContent, margin, yPos, contentWidth, 9);
            if (content.length > 2000) {
              yPos += 2;
              yPos = addWrappedText('... (content truncated)', margin, yPos, contentWidth, 9);
            }
            yPos += 3;
          }
        } catch (err) {
          yPos = addWrappedText('(Content unavailable)', margin, yPos, contentWidth, 9);
          yPos += 3;
        }

        // Comments feature removed

        // Add separator
        yPos += 3;
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      }
    }

    // Comments feature removed

    // Generate filename
    const date = new Date().toISOString().split('T')[0];
    const filename = `workspace_${workspace.name.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.pdf`;

    // Save PDF
    doc.save(filename);
  }
}

export const teamWorkspaceService = TeamWorkspaceService.getInstance();
